import {
  ICopyDefaultToContractBody,
  IDefaultRulePropagationApplyRequest,
  IDefaultRulePropagationApplyResponse,
  IDefaultRulePropagationPreviewResponse,
  IRulesetListParams,
} from "types/DefaultRules/DefaultRules";
import {
  IModifierAdjustmentRuleset,
  IModifierAdjustmentRulesetCreateRequest,
  IModifierAdjustmentRulesetUpdateRequest,
} from "types/ModifierAdjustment/ModifierAdjustment";

import { api } from ".";

interface IListResponse {
  data: IModifierAdjustmentRuleset[];
  message?: string;
}

interface IItemResponse {
  data: IModifierAdjustmentRuleset;
  message?: string;
}

const modifierAdjustmentQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getAllModifierAdjustments: builder.query<
      IListResponse,
      IRulesetListParams | null | void
    >({
      query: (params) => {
        let search = "";
        if (params?.applicability?.length) {
          search = `?applicability=${params.applicability.join(",")}`;
        } else if (params && params.isDefault !== undefined) {
          search = `?isDefault=${params.isDefault}`;
        }
        return `modifier-adjustments/all${search}`;
      },
      providesTags: ["ModifierAdjustmentRulesets"],
    }),
    getModifierAdjustmentById: builder.query<IItemResponse, string>({
      query: (id) => `modifier-adjustments/${id}`,
      providesTags: (_r, _e, id) => [
        { type: "ModifierAdjustmentRulesets", id },
      ],
    }),
    createModifierAdjustment: builder.mutation<
      IItemResponse,
      IModifierAdjustmentRulesetCreateRequest
    >({
      query: (body) => ({
        url: "modifier-adjustments",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ModifierAdjustmentRulesets"],
    }),
    updateModifierAdjustment: builder.mutation<
      IItemResponse,
      IModifierAdjustmentRulesetUpdateRequest
    >({
      query: (body) => ({
        url: `modifier-adjustments/${body.id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["ModifierAdjustmentRulesets", "DefaultRulePropagation"],
    }),
    deleteModifierAdjustment: builder.mutation<void, string>({
      query: (id) => ({
        url: `modifier-adjustments/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ModifierAdjustmentRulesets", "DefaultRulePropagation"],
    }),
    cloneModifierAdjustmentAsDefault: builder.mutation<IItemResponse, string>({
      query: (id) => ({
        url: `modifier-adjustments/${id}/clone-as-default`,
        method: "POST",
      }),
      invalidatesTags: ["ModifierAdjustmentRulesets"],
    }),
    copyModifierAdjustmentToContract: builder.mutation<
      IItemResponse,
      { id: string; body: ICopyDefaultToContractBody }
    >({
      query: ({ id, body }) => ({
        url: `modifier-adjustments/${id}/copy-to-contract`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["ModifierAdjustmentRulesets", "ContractFeeSchedules"],
    }),
    getModifierAdjustmentPropagationPreview: builder.query<
      IDefaultRulePropagationPreviewResponse,
      string
    >({
      query: (id) => `modifier-adjustments/${id}/propagate/preview`,
      providesTags: (_r, _e, id) => [
        { type: "DefaultRulePropagation", id: `modifierAdjustment:${id}` },
      ],
    }),
    applyModifierAdjustmentPropagation: builder.mutation<
      IDefaultRulePropagationApplyResponse,
      { id: string; requests: IDefaultRulePropagationApplyRequest[] }
    >({
      query: ({ id, requests }) => ({
        url: `modifier-adjustments/${id}/propagate`,
        method: "POST",
        body: { requests },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        "ModifierAdjustmentRulesets",
        { type: "DefaultRulePropagation", id: `modifierAdjustment:${id}` },
      ],
    }),
  }),
});

export const {
  useGetAllModifierAdjustmentsQuery,
  useGetModifierAdjustmentByIdQuery,
  useCreateModifierAdjustmentMutation,
  useUpdateModifierAdjustmentMutation,
  useDeleteModifierAdjustmentMutation,
  useCloneModifierAdjustmentAsDefaultMutation,
  useCopyModifierAdjustmentToContractMutation,
  useGetModifierAdjustmentPropagationPreviewQuery,
  useLazyGetModifierAdjustmentPropagationPreviewQuery,
  useApplyModifierAdjustmentPropagationMutation,
} = modifierAdjustmentQueries;
